/**
 * The stub cascade (engine.md §13) as a pure storage operation over a driver, so the device, the
 * server replica and the sync applier all perform it identically:
 *
 *   - nulls the nullable content columns (keeping id, source_id, external_id, received_at,
 *     published_at, sort_at) and sets excerpt = ''; content_hlc is NOT changed (every replica
 *     performs the cascade itself when it applies the life register);
 *   - removes the FTS row;
 *   - physically deletes the message's child rows of every synced table with a parent message
 *     (message_topics, message_tags, events, assessments, message_scores) and their sync bookkeeping
 *     (sync_dirty, sync_seq), and its LOCAL on-device vectors (message_vectors).
 *
 * The caller writes the life register (deleted_at, delete_reason) itself.
 */

import type { SqlDriver } from '../contracts/storage.js';
import { SYNC_TABLES, type SyncTableSpec } from '../contracts/sync.js';
import { MESSAGE_LOCAL_CHILD_TABLES } from './meta.js';

/** The synced child tables of a message, with their parent column and wire-id expression. */
const CHILDREN: ReadonlyArray<{ table: string; parent: string; id: string }> = (SYNC_TABLES as readonly SyncTableSpec[])
  .filter((s) => s.parentMessageColumn !== undefined)
  .map((s) => ({ table: s.table, parent: s.parentMessageColumn as string, id: s.key.join(" || '|' || ") }));

/** Content columns a stub keeps NULL. excerpt is set to ''. */
export const STUB_NULLED_COLUMNS = [
  'url', 'title', 'title_fold', 'author', 'author_fold', 'body_html', 'image_url', 'media', 'lang', 'thread_key', 'extra',
] as const;

/** Wire ids of the child rows of a message, per child table. */
export function childWireIds(db: SqlDriver, messageId: string): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const c of CHILDREN) {
    out[c.table] = db.prepare(`SELECT ${c.id} AS wid FROM ${c.table} WHERE ${c.parent} = ?`).all([messageId]).map((r) => String(r.wid));
  }
  return out;
}

export function applyStubCascade(db: SqlDriver, messageId: string): void {
  const row = db.prepare('SELECT seq FROM messages WHERE id = ?').get([messageId]);
  if (!row) return;
  db.prepare(
    `UPDATE messages SET ${STUB_NULLED_COLUMNS.map((c) => `${c} = NULL`).join(', ')}, excerpt = '' WHERE id = ?`,
  ).run([messageId]);
  db.prepare('DELETE FROM messages_fts WHERE rowid = ?').run([row.seq as number]);
  const children = childWireIds(db, messageId);
  const dropDirty = db.prepare('DELETE FROM sync_dirty WHERE tbl = ? AND id = ?');
  const dropSeq = db.prepare('DELETE FROM sync_seq WHERE tbl = ? AND id = ?');
  for (const [table, ids] of Object.entries(children)) {
    for (const id of ids) {
      dropDirty.run([table, id]);
      dropSeq.run([table, id]);
    }
  }
  for (const c of CHILDREN) db.prepare(`DELETE FROM ${c.table} WHERE ${c.parent} = ?`).run([messageId]);
  for (const t of MESSAGE_LOCAL_CHILD_TABLES) db.prepare(`DELETE FROM ${t} WHERE message_id = ?`).run([messageId]);
  clearStubShown(db, messageId);
}

/**
 * A stub shows no scores (data schema 3): its shown columns are cleared IN PLACE (auto_hlc kept), on every
 * replica, whenever the cascade runs or the auto register of a stub is written — like its content columns.
 * Shown scores are derived per replica from rows the cascade deletes; without this, replicas that derived
 * them from different row sets before the stub arrived would keep different values under one clock value.
 */
export function clearStubShown(db: SqlDriver, messageId: string): void {
  db.prepare(
    `UPDATE messages SET scores = NULL, best_score = NULL, best_topic_id = NULL, scored_at = NULL, scoring_model = NULL, score_state = 'none'
     WHERE id = ? AND deleted_at IS NOT NULL
       AND (scores IS NOT NULL OR best_score IS NOT NULL OR best_topic_id IS NOT NULL OR scored_at IS NOT NULL OR scoring_model IS NOT NULL OR score_state <> 'none')`,
  ).run([messageId]);
}
